import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { computeCappedCanvasSize, computeCircularAspectCorrection, getFontFamily, parseCompiledManifests, parseVisitCheck, recordTargetViewSafely, resolveVideoControlIcon, scaleCanvasPositionToARCoords, scaleCanvasSizeToARCoords, scaleSizeToARCoordsSocial, shouldRecordTargetView, useSetState } from 'utils/functions.utils';
import { IImageTargetManifest, IObject, ObjectTypes, TargetImageObject } from 'utils/interface.utils';
import { Assets, Models, PreviewsLeftCard } from 'utils/imports.utils';
import { STRINGS } from 'constants/strings.constant';
import FontFaceObserver from 'fontfaceobserver';
import { useLocation } from 'react-router-dom';
import "./ar.screen.scss";

const AR = () => {
    const [state, setState] = useSetState({
        projectDataListAr: [],
        visitAllowed: false,
        visitCheckComplete: false,
        unlimitedVisits: false,
        remainingVisits: 0,
        maxVisits: 0,
        loading:true,
        error: null,
        compiledTargetImage: null,
        cameraReady: false,
    })

    const containerRef = useRef<HTMLCanvasElement>(null);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const targetContainerRef = useRef<Array<THREE.Group | null>>([]);
    const videoTexturesRef = useRef<Array<THREE.VideoTexture>>([]);
    const lastRecordedAt = useRef<Record<string, number>>({});
    const projectId = searchParams.get('project_id');

    const checkVisitEligibility = async () => {
        if (!projectId) {
            setState({
                error: 'Project ID is required',
                loading: false
            })
            return
        }

        try {
            const check = await Models.auth.checkVisit({
                project_id: projectId
            })
            const { allowed, unlimited, remainingVisits, maxVisits } = parseVisitCheck(check);

            setState({
                visitCheckComplete: true,
                visitAllowed: allowed,
                unlimitedVisits: unlimited,
                remainingVisits,
                maxVisits,
                loading: false,
            });

        } catch (error: any) {
            console.log("error:", error);
            setState({
                error: 'Failed to check visit eligibility',
                loading: false,
                visitAllowed: false,
                visitCheckComplete: true,
            });
        }
    }

    const renderObjectImageAR = (properties: any, targetWidth: number, targetHeight: number, containedTargetImageDimension: { width: number, height: number }, targetContainer: THREE.Group | null) => {
        const { src, positionX, positionY, rotationX, scaleX, scaleY, renderOrder } = properties;
        const loader = new THREE.TextureLoader();
        loader.load(
            src,
            (texture) => {
                const objectGeometry = new THREE.PlaneGeometry(1, 1);
                const objectMaterial = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false });
                const objectImage = new THREE.Mesh(objectGeometry, objectMaterial);

                const { x: posX, y: posY } = scaleCanvasPositionToARCoords(positionX, positionY, targetWidth, targetHeight, containedTargetImageDimension);
                objectImage.position.set(posX, posY, 0);

                const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
                objectImage.rotation.set(0, 0, rotationXRadians);

                const { x: width, y: height } = scaleCanvasSizeToARCoords(scaleX, scaleY, targetWidth, targetHeight, containedTargetImageDimension);
                objectImage.scale.set(width, height, 1);

                objectImage.renderOrder = renderOrder || 1;

                targetContainer?.add(objectImage);
            },
            undefined,
            (error) => {
                console.error("Image loading error:", error);
            }
        )
    }

    const renderObjectVideoAR = (properties: IObject, targetWidth: number, targetHeight: number, containedTargetImageDimension: { width: number, height: number }, targetContainer: THREE.Group | null) => {
        const { src, positionX, positionY, rotationX, scaleX, scaleY, renderOrder } = properties;

        const videoElement = document.createElement("video");
        videoElement.src = src;
        videoElement.crossOrigin = "Anonymous";
        videoElement.muted = true;
        videoElement.playsInline = true;

        const videoTexture = new THREE.VideoTexture(videoElement);
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.format = THREE.RGBAFormat;
        videoTexturesRef.current.push(videoTexture);

        const objectMaterial = new THREE.MeshBasicMaterial({ map: videoTexture, transparent: true, opacity: 1.0, depthTest: false, depthWrite: false });
        const objectGeometry = new THREE.PlaneGeometry(1, 1);
        const objectVideo = new THREE.Mesh(objectGeometry, objectMaterial);

        const { x: posX, y: posY } = scaleCanvasPositionToARCoords(positionX, positionY, targetWidth, targetHeight,containedTargetImageDimension)
        objectVideo.position.set(posX, posY, 0);

        const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
        objectVideo.rotation.set(0, 0, rotationXRadians);

        const { x: width, y: height } = scaleCanvasSizeToARCoords(scaleX, scaleY, targetWidth, targetHeight, containedTargetImageDimension);
        objectVideo.scale.set(width, height, 1);

        const videoGroup = new THREE.Group();
        videoGroup.add(objectVideo);

        // renderOrder must be set on the actual rendered mesh, not just the (non-sorting) Group --
        // Three.js sorts the transparent render queue per-mesh and does not propagate a parent
        // Group's renderOrder down to its children.
        objectVideo.renderOrder = renderOrder || 1;

        targetContainer?.add(videoGroup)

        let isPlaying = false;

        const createControlButton = () => {
            const loader = new THREE.TextureLoader();
            const playTexture = loader.load(Assets.video_play);
            const pauseTexture = loader.load(Assets.video_pause);

            const buttonMaterial = new THREE.MeshBasicMaterial({
                map: playTexture,
                transparent: true,
                depthTest: false,
                depthWrite: false
            });

            // buttonToVideoRatio is a ratio *of the video's own rendered size* -- it was
            // previously used directly as an absolute PlaneGeometry size, ignoring the video's
            // actual width/height entirely, which made the button look oversized on smaller
            // videos (and undersized on larger ones).
            const buttonToVideoRatio = 0.1;
            const buttonSize = Math.min(width, height) * buttonToVideoRatio;

            // targetContainer's scale is the detected marker's own aspect ratio
            // (aspectScale.x/aspectScale.y from onImageFound), which is non-uniform for any
            // non-square target image. A plain square PlaneGeometry inherits that non-uniform
            // scale and renders as an oval, not a circle. To land back on a true circle on
            // screen we need geomWidth * scaleX == geomHeight * scaleY, i.e. the height factor
            // must be scaleX / scaleY (not scaleY / scaleX, which makes the distortion worse).
            const containerScale = targetContainer?.scale ?? { x: 1, y: 1 };
            const aspectCorrection = computeCircularAspectCorrection(containerScale.x, containerScale.y);
            const buttonGeometry = new THREE.PlaneGeometry(buttonSize, buttonSize * aspectCorrection);
            const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial)
            // The AR materials have depthTest:false, so draw order is only renderOrder. The video mesh takes the
            // object's saved renderOrder (3+ for a video added after other objects); a fixed value here left the
            // button underneath it. Sit just above the video's own layer, below the next whole layer.
            const controlButtonRenderOrderOffset = 0.5;
            buttonMesh.renderOrder = objectVideo.renderOrder + controlButtonRenderOrderOffset;

            buttonMesh.position.copy(objectVideo.position);
            buttonMesh.position.z = objectVideo.position.z + 0.01;

            // Tap-to-toggle: the icon must not sit over a playing video. Paused -> a semi-transparent
            // play icon stays up so the user knows to tap; playing -> the icon flashes, then fades out.
            const pausedIconOpacity = 0.7;
            const iconFlashMs = 400;
            const iconFadeMs = 300;
            let fadeTimeoutId: ReturnType<typeof setTimeout> | undefined;
            let fadeFrameId: number | undefined;

            const cancelIconFade = () => {
                if (fadeTimeoutId !== undefined) clearTimeout(fadeTimeoutId);
                if (fadeFrameId !== undefined) cancelAnimationFrame(fadeFrameId);
                fadeTimeoutId = undefined;
                fadeFrameId = undefined;
            };

            const updateButtonTexture = () => {
                const { icon, persistent } = resolveVideoControlIcon(isPlaying);
                cancelIconFade();
                buttonMaterial.map = icon === 'pause' ? pauseTexture : playTexture;
                buttonMaterial.needsUpdate = true;
                buttonMaterial.opacity = pausedIconOpacity;
                buttonMesh.visible = true;
                if (persistent) return;

                fadeTimeoutId = setTimeout(() => {
                    const fadeStart = performance.now();
                    const step = (now: number) => {
                        const progress = Math.min((now - fadeStart) / iconFadeMs, 1);
                        buttonMaterial.opacity = pausedIconOpacity * (1 - progress);
                        if (progress < 1) {
                            fadeFrameId = requestAnimationFrame(step);
                        } else {
                            buttonMesh.visible = false;
                            fadeFrameId = undefined;
                        }
                    };
                    fadeFrameId = requestAnimationFrame(step);
                }, iconFlashMs);
            };

            const togglePlayback = () => {
                if (!isPlaying) {
                    videoElement.play().then(() => {
                        isPlaying = true;
                        updateButtonTexture();
                        console.log("Video started playing");
                    }).catch(error => {
                        console.error("Video playback error:", error);
                    });
                } else {
                    videoElement.pause();
                    isPlaying = false;
                    updateButtonTexture();
                    console.log("Video paused");
                }
            };

            // The whole video surface is the tap target, so users can tap anywhere on the video
            // (not just the small icon) to play/pause. A unit plane copying the video mesh's
            // transform lines up with it exactly, whatever its size and rotation.
            const hitAreaGeometry = new THREE.PlaneGeometry(1, 1);
            const hitAreaMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthTest: false, depthWrite: false });
            const hitAreaMesh = new THREE.Mesh(hitAreaGeometry, hitAreaMaterial);
            hitAreaMesh.position.copy(objectVideo.position);
            hitAreaMesh.position.z = objectVideo.position.z + 0.02;
            hitAreaMesh.scale.copy(objectVideo.scale);
            hitAreaMesh.rotation.copy(objectVideo.rotation);
            hitAreaMesh.renderOrder = 3;
            hitAreaMesh.raycast = THREE.Mesh.prototype.raycast;
            hitAreaMesh.userData = {
                type: 'VIDEO CONTROL',
                videoElement: videoElement,
                clickable: true,
                togglePlayback
            };

            videoGroup.add(buttonMesh);
            videoGroup.add(hitAreaMesh);
            updateButtonTexture();

            // The video is not looped: once it finishes, bring the play icon back.
            videoElement.addEventListener('ended', () => {
                isPlaying = false;
                updateButtonTexture();
            });

            return {
                mesh: hitAreaMesh,
                updateTexture: updateButtonTexture,
                show: () => { buttonMesh.visible = true; hitAreaMesh.visible = true; },
                hide: () => { cancelIconFade(); buttonMesh.visible = false; hitAreaMesh.visible = false; },
            }
        }

        const controlButton = createControlButton();
        videoGroup.userData.controlButton = controlButton;

        return controlButton.mesh;
    }

    const renderObjectButtonAR = (properties: IObject, targetWidth: number, targetHeight: number, containedTargetImageDimension: { width: number, height: number }, targetContainer: THREE.Group | null) => {
        const {
            presets, fontSize, fontFamily, url, text, fontStyle, fontColor, bgColor,
            positionX, positionY, scaleX, scaleY, rotationX, renderOrder
        } = properties;


        const textColor = fontColor || (presets?.color) || "#000000";
        const backgroundColor = bgColor || (presets?.backgroundColor) || "#FFFFFF";
        const borderRadius = presets?.borderRadius || 5;
        const borderWidth = presets?.borderWidth || 0;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            console.error("Failed to get 2D context");
            return null;
        }

        const baseFontSize = fontSize || 12;
        const scaledFontSize = baseFontSize;
        const isBold = fontStyle && fontStyle.includes("bold");
        const isItalic = fontStyle && fontStyle.includes("italic");
        const isUnderline = fontStyle && fontStyle.includes("underline");

        let fontStyleString = "";
        if (isBold) fontStyleString += "bold ";
        if (isItalic) fontStyleString += "italic ";

        const family = typeof getFontFamily === 'function'
            ? getFontFamily(fontFamily, fontStyle)
            : (fontFamily || "Arial");

        // Painted once straight away, then again when the font face finishes loading: @font-face
        // faces are lazy and /ar has no DOM text asking for them, so the first paint falls back.
        const paint = () => {
            ctx.font = `${fontStyleString}${scaledFontSize}px ${family}`;

            const textMetrics = ctx.measureText(text);
            const textWidth = textMetrics.width;

            const scale = 2;
            canvas.width = scaleX * scale;
            canvas.height = scaleY * scale;
            ctx.scale(scale, scale);

            ctx.font = `${fontStyleString}${scaledFontSize}px ${family}`;

            ctx.fillStyle = backgroundColor;

            if (typeof ctx.roundRect === 'function') {
                ctx.beginPath();
                ctx.roundRect(0, 0, scaleX, scaleY, borderRadius);
                ctx.fill();
            } else {
                drawRoundedRect(ctx, 0, 0, scaleX, scaleY, borderRadius);
            }

            if (borderWidth > 0) {
                ctx.strokeStyle = textColor;
                ctx.lineWidth = borderWidth;

                const halfBorder = borderWidth / 2;
                const borderAdjustment = borderWidth;

                if (typeof ctx.roundRect === 'function') {
                    ctx.beginPath();
                    ctx.roundRect(
                        halfBorder,
                        halfBorder,
                        scaleX - borderAdjustment,
                        scaleY - borderAdjustment,
                        borderRadius
                    );
                } else {
                    drawRoundedRect(
                        ctx,
                        halfBorder,
                        halfBorder,
                        scaleX - borderAdjustment,
                        scaleY - borderAdjustment,
                        borderRadius
                    );
                }

                ctx.lineJoin = "round";
                ctx.stroke();
            }

            ctx.fillStyle = textColor;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(text, scaleX / 2, scaleY / 2);

            if (isUnderline) {
                const underlineY = scaleY / 2 + scaledFontSize / 4;

                ctx.beginPath();
                ctx.moveTo(scaleX / 2 - textWidth / 2, underlineY);
                ctx.lineTo(scaleX / 2 + textWidth / 2, underlineY);
                ctx.lineWidth = 2;
                ctx.strokeStyle = textColor;
                ctx.stroke();
            }

        };
        paint();

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const buttonGeometry = new THREE.PlaneGeometry(1, 1);
        const buttonMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });

        const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);
        buttonMesh.renderOrder = renderOrder || 0;
        buttonMesh.visible = true;
        buttonMesh.material.transparent = true;

        buttonMesh.raycast = THREE.Mesh.prototype.raycast;

        const { x: posX, y: posY } = scaleCanvasPositionToARCoords(positionX, positionY, targetWidth, targetHeight,containedTargetImageDimension)
        buttonMesh.position.set(posX, posY, 0);
        const {x , y , z} = scaleCanvasSizeToARCoords(scaleX, scaleY, targetWidth, targetHeight, containedTargetImageDimension);
        buttonMesh.scale.set(x, y, z);

        const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
        buttonMesh.rotation.set(0, 0, rotationXRadians);

        buttonMesh.userData = {
            type: ObjectTypes.BUTTON,
            url: url || "",
            clickable: true
        }

        if (targetContainer) targetContainer.add(buttonMesh);

        function drawRoundedRect(
            ctx: CanvasRenderingContext2D,
            x: number,
            y: number,
            width: number,
            height: number,
            radius: number
        ) {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
            ctx.fill();
        }

        const loadAndRender = () => {
            if (typeof FontFaceObserver !== 'undefined') {
                const font = new FontFaceObserver(family);
                font.load(null, 5000)
                    .then(() => {
                        console.log("Font loaded successfully:", family);
                        paint();
                        texture.needsUpdate = true;
                    })
                    .catch(() => {
                        console.warn("Font failed to load:", family);
                    });
            } else {
                console.warn("Font failed to load:", family);
            }
        };

        loadAndRender();

        return buttonMesh;
    }

    const renderObjectTextAR = (properties: IObject, targetWidth: number, targetHeight: number, containedTargetImageDimension: { width: number, height: number }, targetContainer: THREE.Group | null) => {
        const { text, fontFamily, fontSize, fontStyle, fontColor, bgColor, positionX, positionY, rotationX, renderOrder, scaleX, scaleY } = properties;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            console.error("Failed to get 2D context");
            return;
        };

        const baseFontSize = fontSize;
        const isBold = fontStyle && fontStyle.includes("bold");
        const isItalic = fontStyle && fontStyle.includes("italic");
        const isUnderline = fontStyle && fontStyle.includes("underline");

        let fontStyleString = "";
        if (isBold) fontStyleString += "bold ";
        if (isItalic) fontStyleString += "italic ";

        const family = getFontFamily(fontFamily, fontStyle);
        ctx.font = `${fontStyleString} ${baseFontSize}px ${family}`;

        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;

        const bgWidth = scaleX;
        const bgHeight = scaleY;

        // Use scale = 1 for clarity but compensate with larger font size
        const scale = 1;
        canvas.width = bgWidth * scale;
        canvas.height = bgHeight * scale;

        // No need to scale if scale = 1
        if (scale !== 1) {
            ctx.scale(scale, scale);
        }

        // Apply the background color if it exists
        if (bgColor && bgColor !== "transparent") {
            ctx.fillStyle = bgColor;
            ctx.roundRect(0, 0, bgWidth, bgHeight, 8);
            ctx.fill();
        }

        const drawText = () => {
            // Ensure font is set correctly
            ctx.font = `${fontStyleString} ${baseFontSize}px ${family}`;
            ctx.fillStyle = fontColor;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            // Enable text sharpening
            ctx.imageSmoothingEnabled = false;

            ctx.fillText(text, bgWidth / 2, bgHeight / 2);

            if (isUnderline) {
                const underlineY = bgHeight / 2 + baseFontSize / 2 + 1;

                ctx.beginPath();
                ctx.moveTo(bgWidth / 2 - textWidth / 2, underlineY);
                ctx.lineTo(bgWidth / 2 + textWidth / 2, underlineY);
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = fontColor;
                ctx.stroke();
            }
        }

        if (typeof FontFaceObserver !== "undefined") {
            const font = new FontFaceObserver(family);
            font.load(null, 5000).then(
                () => {
                    console.log("Font loaded successfully:", family);
                    drawText();

                    // Create and return sprite after text is drawn
                    return createAndReturnMesh();
                },
                () => {
                    console.warn("Font failed to load:", family);
                    drawText();

                    // Create and return sprite after text is drawn
                    return createAndReturnMesh();
                }
            );
        } else {
            drawText();
            return createAndReturnMesh();
        }

        function createAndReturnMesh() {
            // Create a texture from the canvas
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;

            // Set texture filtering for crisp text
            texture.minFilter = THREE.NearestFilter;
            texture.magFilter = THREE.NearestFilter;

            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false
            });

            const { x: width, y: height } = scaleCanvasSizeToARCoords(bgWidth, bgHeight, targetWidth, targetHeight, containedTargetImageDimension);

            const planeGeometry = new THREE.PlaneGeometry(width, height);
            const mesh = new THREE.Mesh(planeGeometry, material);


            const { x: posX, y: posY } = scaleCanvasPositionToARCoords(positionX, positionY, targetWidth, targetHeight, containedTargetImageDimension)
            mesh.position.set(posX, posY, 0);

            const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
            mesh.rotation.set(0, 0, rotationXRadians);

            mesh.renderOrder = renderOrder || 1;

            targetContainer?.add(mesh);
        }
    }

    const renderObjectSocialAR = async (properties: IObject, targetWidth: number, targetHeight: number, containedTargetImageDimension: { width: number, height: number }, targetContainer: THREE.Group | null) => {
        const { src, color, bgColor, url, positionX, positionY, rotationX, scaleX, scaleY, renderOrder } = properties;

        const { x: width } = scaleSizeToARCoordsSocial(scaleX, scaleY, targetWidth);

        const iconBgWidth = width;

        const socialGroup = new THREE.Group();
        socialGroup.renderOrder = renderOrder;

        // targetContainer's scale is non-uniform for any non-square target image (see
        // createControlButton's identical fix above) -- correct the group's own local scale so
        // the circular background and icon plane inside it render as a true circle, not an oval.
        const containerScale = targetContainer?.scale ?? { x: 1, y: 1 };
        const aspectCorrection = computeCircularAspectCorrection(containerScale.x, containerScale.y);
        socialGroup.scale.set(1, aspectCorrection, 1);

        // The correction above and the object's own rotation must NOT share a node. An Object3D
        // composes its local matrix as T*R*S, so putting both on socialGroup gives
        // container * rotation * correction -- the correction lands inside the rotation, and the
        // corrected circle gets stretched again along the container's fixed axes at every angle
        // except 0. Rotating a child instead makes the chain container * correction * rotation,
        // where container * correction is a uniform scale, so the icon stays circular at any angle.
        const socialContent = new THREE.Group();
        socialGroup.add(socialContent);

        const iconBgGeometry = new THREE.CircleGeometry(iconBgWidth, 32);
        const iconBgMaterial = new THREE.MeshBasicMaterial({
            color: bgColor,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });
        const iconBgMesh = new THREE.Mesh(iconBgGeometry, iconBgMaterial);
        // Must use the real renderOrder value, not a hardcoded constant -- socialGroup.renderOrder
        // (above) is a no-op for sorting since it's a Group, not a rendered mesh.
        iconBgMesh.renderOrder = renderOrder;

        socialContent.add(iconBgMesh);

        if (targetContainer) targetContainer.add(socialGroup);

        // Set the position of the social icon
        const { x, y } = scaleCanvasPositionToARCoords(positionX, positionY, targetWidth, targetHeight, containedTargetImageDimension)
        socialGroup.position.set(x, y, 0);

        const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
        socialContent.rotation.set(0, 0, rotationXRadians);

        // handleClick raycasts with `recursive: true`, so the hit object is always a
        // child mesh (iconBgMesh/iconMesh), never socialGroup itself -- userData must
        // live on the meshes too, not just the group, or the click handler can't read it.
        const socialUserData = {
            type: ObjectTypes.SOCIAL,
            url: url || "",
            clickable: true
        };
        socialGroup.userData = socialUserData;
        socialContent.userData = socialUserData;
        iconBgMesh.userData = socialUserData;

        try {
            const texture = await createColoredSVGTexture(src, color);
            const iconWidth = iconBgWidth;
            const iconHeight = iconBgWidth;

            const iconGeometry = new THREE.PlaneGeometry(iconWidth, iconHeight);
            const iconMaterial = new THREE.MeshBasicMaterial({
                map: texture as THREE.Texture,
                transparent: true,
                depthTest: false,
                depthWrite: false
            });

            const iconMesh = new THREE.Mesh(iconGeometry, iconMaterial);
            // Same renderOrder as iconBgMesh (real value, not hardcoded) -- iconMesh's z offset
            // (0.01, see below) is what keeps it drawn above the background within the tie.
            iconMesh.renderOrder = renderOrder;
            iconMesh.position.set(0, 0, 0.01);
            iconMesh.userData = socialUserData;

            socialContent.add(iconMesh);

        } catch (error) {
            console.error("Error creating icon:", error);
        }

        return socialGroup;
    };
    // Create colored SVG texture
    const createColoredSVGTexture = (svgUrl: string, colorValue: THREE.Color) => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const iconWidth = 28;
            const iconHeight = 28;

            canvas.width = iconWidth;
            canvas.height = iconHeight;

            if (!ctx) {
                reject(new Error('Canvas 2D context not available'));
                return;
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;

                const colorObj = new THREE.Color(colorValue);
                const r = Math.floor(colorObj.r * 255);
                const g = Math.floor(colorObj.g * 255);
                const b = Math.floor(colorObj.b * 255);

                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] > 0) {
                        data[i] = r;
                        data[i + 1] = g;
                        data[i + 2] = b;
                    }
                }

                ctx.putImageData(imageData, 0, 0);

                const texture = new THREE.CanvasTexture(canvas);
                texture.needsUpdate = true;
                resolve(texture);
            };

            img.onerror = (error) => {
                reject(error);
            };

            img.src = svgUrl;
        });
    };

    const getManyProjectData = async () => {
        if (!projectId) return;
        try {
            // Public, unauthenticated -- anonymous QR-code scanners have no auth token, so this
            // must never call an auth-gated endpoint (see augmo-web/ISSUES.md Phase 5 section).
            const res: any = await Models.auth.resolveProjectData({
                project_id: projectId,
            });
            if (res && res.data) {
                setState({ projectDataListAr: res.data.targets, compiledTargetImage: res.data.complied_target_image });
            } else {
                console.error('No project data found');
            }
        } catch (error) {
            console.error('Error fetching project data:', error);
        }
    };

    const createTargetContainer = (index: number, scene: THREE.Scene, aspectScale: { x: number, y: number }) => {
        const targetContainer = new THREE.Group();
        targetContainer.userData.aspectScale = aspectScale;
        targetContainer.scale.set(aspectScale.x, aspectScale.y, 1);
        targetContainer.position.set(0, 0, 0);
        targetContainer.renderOrder = 0;
        targetContainerRef.current[index] = targetContainer;
        scene.add(targetContainer);
    }

    useEffect(() => {
        if (containerRef.current === null || state.projectDataListAr.length === 0 || !state.compiledTargetImage) return;

        const manifests: IImageTargetManifest[] = parseCompiledManifests(state.compiledTargetImage);
        if (manifests.length === 0) return;

        targetContainerRef.current = [];
        videoTexturesRef.current = [];

        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        const clickableObjects: any = [];
        let sceneRef: THREE.Scene | null = null;
        let cameraRef: THREE.Camera | null = null;
        let rendererRef: THREE.WebGLRenderer | null = null;
        let arStarted = false;

        const buildTargetOverlays = async (index: number, targetContainer: THREE.Group, targetWidth: number, targetHeight: number) => {
            const targetImageId = manifests[index]?.metadata?.targetImageId;
            const targetImageData: any = targetImageId
                ? state.projectDataListAr.find((item: any) => item.data?.id === targetImageId)
                : state.projectDataListAr[index];
            if (!targetImageData) return;
            const containedTargetImageDimension = targetImageData.data.containedTargetImageDimension;
            for (const object of targetImageData.data.objects as TargetImageObject[]) {
                if (object.type === ObjectTypes.IMAGE) {
                    renderObjectImageAR(object.properties, targetWidth, targetHeight, containedTargetImageDimension, targetContainer);
                } else if (object.type === ObjectTypes.VIDEO) {
                    const videoMeshControl = renderObjectVideoAR(object.properties, targetWidth, targetHeight, containedTargetImageDimension, targetContainer);
                    if (videoMeshControl) clickableObjects.push(videoMeshControl);
                } else if (object.type === ObjectTypes.BUTTON) {
                    const buttonMesh = renderObjectButtonAR(object.properties, targetWidth, targetHeight, containedTargetImageDimension, targetContainer);
                    if (buttonMesh) clickableObjects.push(buttonMesh);
                } else if (object.type === ObjectTypes.TEXT) {
                    renderObjectTextAR(object.properties, targetWidth, targetHeight, containedTargetImageDimension, targetContainer);
                } else {
                    const socialGroup = await renderObjectSocialAR(object.properties, targetWidth, targetHeight, containedTargetImageDimension, targetContainer);
                    if (socialGroup) clickableObjects.push(socialGroup);
                }
            }
        }

        const applyEventTransform = (group: THREE.Group, detail: XRImageEventDetail) => {
            group.position.set(detail.position.x, detail.position.y, detail.position.z);
            group.quaternion.set(detail.rotation.x, detail.rotation.y, detail.rotation.z, detail.rotation.w);
            const aspectScale = group.userData.aspectScale || { x: 1, y: 1 };
            group.scale.set(detail.scale * aspectScale.x, detail.scale * aspectScale.y, detail.scale);
        }

        const handleClick = (event: any) => {
            event.preventDefault();
            event.stopPropagation();

            if (!rendererRef || !cameraRef) return;

            let clientX, clientY;

            if (event.type === 'touchend') {
                if (event.changedTouches && event.changedTouches[0]) {
                    clientX = event.changedTouches[0].clientX;
                    clientY = event.changedTouches[0].clientY;
                }
            } else if (event.type === 'click') {
                clientX = event.clientX;
                clientY = event.clientY;
            }

            if (clientX === undefined || clientY === undefined) return;

            const rect = rendererRef.domElement.getBoundingClientRect();
            mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, cameraRef);
            const intersects = raycaster.intersectObjects(clickableObjects, true);

            if (intersects.length > 0) {
                const clickedObject = intersects[0].object;
                const userData = clickedObject.userData;
                if (userData && userData.type === 'VIDEO CONTROL') {
                    if (userData.togglePlayback) userData.togglePlayback();
                } else if (userData && userData.url) {
                    window.open(userData.url, '_blank');
                }
            }
        };

        const onImageFound = (detail: XRImageEventDetail) => {
            const index = manifests.findIndex((manifest) => manifest.name === detail.name);
            if (index === -1 || !sceneRef) return;

            let targetContainer = targetContainerRef.current[index];
            if (!targetContainer) {
                const properties: any = manifests[index].properties || {};
                const targetWidth = properties.width || 1;
                const targetHeight = properties.height || 1;
                const aspectScale = { x: detail.scaledWidth, y: detail.scaledHeight };
                createTargetContainer(index, sceneRef, aspectScale);
                targetContainer = targetContainerRef.current[index];
                // Intentionally not awaited: overlays populate clickableObjects in the background,
                // while target positioning/visibility below must apply immediately on every frame.
                if (targetContainer) void buildTargetOverlays(index, targetContainer, targetWidth, targetHeight);
            }
            if (targetContainer) {
                applyEventTransform(targetContainer, detail);
                targetContainer.visible = true;
            }

            const targetId = manifests[index]?.target_id;
            if (targetId && projectId) {
                const now = Date.now();
                if (shouldRecordTargetView(lastRecordedAt.current, targetId, now)) {
                    lastRecordedAt.current[targetId] = now;
                    recordTargetViewSafely(() => Models.targetView.recordView({ target_id: targetId, project_id: projectId }));
                }
            }
        };

        const onImageUpdated = (detail: XRImageEventDetail) => {
            const index = manifests.findIndex((manifest) => manifest.name === detail.name);
            const targetContainer = index !== -1 ? targetContainerRef.current[index] : null;
            if (targetContainer) applyEventTransform(targetContainer, detail);
        };

        const onImageLost = (detail: XRImageEventDetail) => {
            const index = manifests.findIndex((manifest) => manifest.name === detail.name);
            const targetContainer = index !== -1 ? targetContainerRef.current[index] : null;
            if (targetContainer) targetContainer.visible = false;
        };

        const imageTrackingPipelineModule = (): XR8CameraPipelineModule => ({
            name: 'augmo-image-tracking',
            listeners: [
                { event: 'reality.imagefound', process: (event) => onImageFound(event.detail) },
                { event: 'reality.imageupdated', process: (event) => onImageUpdated(event.detail) },
                { event: 'reality.imagelost', process: (event) => onImageLost(event.detail) },
            ],
            onStart: ({ canvas }) => {
                if (!window.XR8) return;
                const { scene, camera, renderer } = window.XR8.Threejs.xrScene();
                sceneRef = scene;
                cameraRef = camera;
                rendererRef = renderer;

                const resolvedManifests: IImageTargetManifest[] = manifests.map((manifest) => ({
                    ...manifest,
                    imagePath: manifest.resources.luminanceImage || manifest.imagePath,
                }));
                window.XR8.XrController.configure({ imageTargetData: resolvedManifests });

                canvas.addEventListener('click', handleClick, { passive: false });
                canvas.addEventListener('touchend', handleClick, { passive: false });

                arStarted = true;
                setState({ cameraReady: true });
            },
            onUpdate: () => {
                videoTexturesRef.current.forEach((videoTexture) => {
                    const videoElement = videoTexture.image as HTMLVideoElement;
                    if (videoElement && videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
                        videoTexture.needsUpdate = true;
                    }
                });
            },
        });

        const MAX_CANVAS_DIMENSION = 1920;

        const resizeCanvas = () => {
            if (!containerRef.current) return;
            const dpr = window.devicePixelRatio || 1;
            const { width, height } = computeCappedCanvasSize(window.innerWidth, window.innerHeight, dpr, MAX_CANVAS_DIMENSION);
            containerRef.current.width = width;
            containerRef.current.height = height;
        };

        const startAr = () => {
            if (!window.XR8 || !containerRef.current) return;
            window.THREE = THREE;
            resizeCanvas();
            window.XR8.addCameraPipelineModules([
                window.XR8.GlTextureRenderer.pipelineModule(),
                window.XR8.Threejs.pipelineModule(),
                window.XR8.XrController.pipelineModule(),
                imageTrackingPipelineModule(),
            ]);
            window.XR8.run({ canvas: containerRef.current });
            window.addEventListener('resize', resizeCanvas);
        };

        if (window.XR8) {
            startAr();
        } else {
            window.addEventListener('xrloaded', startAr);
        }

        return () => {
            window.removeEventListener('xrloaded', startAr);
            window.removeEventListener('resize', resizeCanvas);
            if (rendererRef?.domElement) {
                rendererRef.domElement.removeEventListener('click', handleClick);
                rendererRef.domElement.removeEventListener('touchend', handleClick);
            }
            if (containerRef.current) {
                containerRef.current.removeEventListener('click', handleClick);
                containerRef.current.removeEventListener('touchend', handleClick);
            }
            if (arStarted && window.XR8) window.XR8.stop();
        }
    }, [state.projectDataListAr, state.compiledTargetImage])

    useEffect(() => {
        checkVisitEligibility()
    },[projectId])

    useEffect(() => {
        if (state.visitAllowed && state.visitCheckComplete) getManyProjectData()
    },[state.visitAllowed,state.visitCheckComplete])

    useEffect(() => {
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        const previousContent = viewportMeta?.getAttribute('content') ?? null;
        viewportMeta?.setAttribute('content', 'width=device-width, initial-scale=0.49');

        return () => {
            if (previousContent !== null) viewportMeta?.setAttribute('content', previousContent);
        };
    },[])

    if (state.loading) {
        return (
            <div style={{
                width: "100vw",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#000",
                color: "#fff"
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    // Render visit limit exceeded
    if (state.visitCheckComplete && !state.visitAllowed) {
        return (
            <div className="ar_restricted">
                <div className="ar_restricted_container">
                    <img src={Assets.lock_lime} alt={STRINGS.ar.lockedAlt} className="ar_restricted_lock" />
                    <h2 className="ar_restricted_pri_h">{STRINGS.ar.restrictedTitle}</h2>
                    <p className="ar_restricted_desc">{STRINGS.ar.restrictedDesc}</p>
                </div>
                <img src={Assets.logo_cream} alt={STRINGS.brand.name} className="ar_restricted_brand" />
            </div>
        );
    }

    return (
        <div className="ar_screen_container">
            <canvas style={{ width: "100%", height: "100%" }} ref={containerRef}></canvas>
            {!state.unlimitedVisits && (
                <PreviewsLeftCard className="ar_screen_previews_left" remaining={state.remainingVisits} max={state.maxVisits} />
            )}
            {state.cameraReady && (
                <img
                    src={Assets.logo_cream}
                    alt={STRINGS.brand.name}
                    className="ar_screen_watermark"
                />
            )}
        </div>
    )
}

export default AR;
